import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowPathIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { integrationToolbarPrimaryButton } from '../../constants/integrationLogsUi';

interface AllocationEmployeeSyncStats {
  isRunning: boolean;
  lastRun: string | null;
  fetched: number;
  skipped: number;
  unmatched: number;
  created: number;
  updated: number;
  failed: number;
  lastError: string | null;
  serviceEnabled?: boolean;
}

const AllocationEmployeeSync: React.FC = () => {
  const queryClient = useQueryClient();
  const { formatDateTime } = useDateFormatter();
  const { hasAnyRole } = useAuth();
  const canSync = hasAnyRole(['admin', 'super_admin']);

  const { data: statusData, isLoading } = useQuery({
    queryKey: ['allocation-employees-sync-status'],
    enabled: canSync,
    queryFn: async () => {
      const response = await api.get('/allocation-employees/sync/status');
      return response.data.data as AllocationEmployeeSyncStats;
    },
    refetchOnWindowFocus: false,
    staleTime: 15000,
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/allocation-employees/sync');
      return response.data.data as AllocationEmployeeSyncStats;
    },
    onSuccess: (stats) => {
      queryClient.setQueryData(['allocation-employees-sync-status'], (prev: AllocationEmployeeSyncStats | undefined) => ({
        ...(prev || {}),
        ...stats,
      }));
      queryClient.invalidateQueries({ queryKey: ['allocation-employees-sync-status'] });
      toast.success(
        `Sync completed: ${stats.fetched} fetched, ${stats.skipped} skipped, ${stats.unmatched ?? 0} unmatched, ${stats.created} created, ${stats.updated} updated`
      );
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Employee allocation sync failed');
    },
  });

  const stats = statusData;
  const isBusy = Boolean(stats?.isRunning || syncMutation.isPending);

  if (!canSync) {
    return (
      <div className="p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <p className="text-amber-900">Only administrators can sync employee allocation data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-indigo-50 p-2">
              <UserGroupIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Employee Allocation Sync</h1>
              <p className="text-sm text-gray-500 mt-1">
                Employee allocation data is provisioned automatically on first SSO login. Use manual sync
                to backfill or refresh records from the Employee Allocation Detail API.
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading sync status…</p>
          ) : (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-gray-500">Service enabled</dt>
                <dd className="font-medium text-gray-900">
                  {stats?.serviceEnabled ? 'Yes' : 'No (set EMPLOYEE_ALLOCATION_ENABLED=true on backend)'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Last sync</dt>
                <dd className="font-medium text-gray-900">
                  {stats?.lastRun ? formatDateTime(stats.lastRun) : 'Never'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Last run — fetched</dt>
                <dd className="font-medium text-gray-900">{stats?.fetched ?? 0}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Last run — skipped (not in Users)</dt>
                <dd className="font-medium text-gray-900">{stats?.skipped ?? 0}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Last run — unmatched (no User link)</dt>
                <dd className="font-medium text-gray-900">{stats?.unmatched ?? 0}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Last run — created / updated</dt>
                <dd className="font-medium text-gray-900">
                  {stats?.created ?? 0} / {stats?.updated ?? 0}
                </dd>
              </div>
              {stats?.failed ? (
                <div>
                  <dt className="text-gray-500">Last run — failed rows</dt>
                  <dd className="font-medium text-red-700">{stats.failed}</dd>
                </div>
              ) : null}
              {stats?.lastError ? (
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Last error</dt>
                  <dd className="font-medium text-red-700">{stats.lastError}</dd>
                </div>
              ) : null}
            </dl>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={() => syncMutation.mutate()}
              disabled={isBusy}
              className={integrationToolbarPrimaryButton}
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${isBusy ? 'animate-spin' : ''}`} />
              {isBusy ? 'Syncing…' : 'Sync employee data'}
            </button>
            <p className="text-xs text-gray-500">
              Safe to run repeatedly — existing employees are updated, not duplicated.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllocationEmployeeSync;
